// frontend/src/components/admin/ActionsPrivilegiees.jsx
// ============================================================================
// ACTIONS PRIVILEGIEES
// ----------------------------------------------------------------------------
// Ce que le centre a fait aux clients : blocages, deblocages, suppressions,
// mots de passe remis, licences changees, versions deployees. Qui, quoi, quand,
// depuis quelle adresse. C'est la reponse quand un client conteste, six mois
// plus tard, une coupure ou un changement d'offre.
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ShieldAlert, RefreshCw, Ban, Unlock, Trash2, KeyRound,
  Gauge, ArrowUpCircle, Rocket, Receipt, Database, User
} from 'lucide-react';
import superAdminApi from '../../services/superAdminApi';
import { useTheme } from '../../contexts/ThemeContext';

const ICONES = {
  COMPANY_CREATE: Database,
  COMPANY_UPDATE: Database,
  COMPANY_BLOCK: Ban,
  COMPANY_UNBLOCK: Unlock,
  COMPANY_DELETE: Trash2,
  USER_PASSWORD_RESET: KeyRound,
  LICENCE_LIMITS: Gauge,
  LICENCE_UPGRADE: ArrowUpCircle,
  LICENCE_VERSION_GLOBAL: Rocket,
  UPDATE_BILLING_CONFIG: Receipt,
  MIGRATIONS_RUN: Database
};

const FENETRES = [
  { valeur: 30, libelle: '30 jours' },
  { valeur: 90, libelle: '90 jours' },
  { valeur: 365, libelle: '1 an' },
  { valeur: 0, libelle: 'Tout' }
];

const champStyle = (t) => ({
  background: t.bgInput, border: `1px solid ${t.border}`, borderRadius: 10,
  padding: '9px 12px', fontSize: 13, color: t.text, outline: 'none', fontFamily: 'inherit'
});

const ActionsPrivilegiees = () => {
  const t = useTheme();

  const [lignes, setLignes] = useState([]);
  const [libelles, setLibelles] = useState({});
  const [sensibles, setSensibles] = useState([]);
  const [entreprises, setEntreprises] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);

  const [action, setAction] = useState('');
  const [entreprise, setEntreprise] = useState('');
  const [fenetre, setFenetre] = useState(90);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const r = await superAdminApi.actions.liste({
        action, company_id: entreprise, jours: fenetre, limit: 300
      });
      setLignes(r.data || []);
      setLibelles(r.libelles || {});
      setSensibles(r.sensibles || []);
    } catch (e) {
      setErreur(e.message || 'Lecture impossible');
    } finally {
      setChargement(false);
    }
  }, [action, entreprise, fenetre]);

  useEffect(() => { charger(); }, [charger]);

  useEffect(() => {
    superAdminApi.companies.getAll()
      .then((r) => setEntreprises(r.data || []))
      .catch(() => setEntreprises([]));
  }, []);

  const nbSensibles = useMemo(
    () => lignes.filter((l) => sensibles.includes(l.action)).length,
    [lignes, sensibles]
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: t.text, margin: 0,
            display: 'flex', alignItems: 'center', gap: 9 }}>
            <ShieldAlert size={20} style={{ color: t.primary }} />
            Actions privilégiées
          </h2>
          <p style={{ fontSize: 13, color: t.textMuted, margin: '4px 0 0' }}>
            Ce que le centre a fait aux clients — {lignes.length} acte(s) sur la période,
            dont {nbSensibles} sensible(s).
          </p>
        </div>
        <button type="button" onClick={charger}
          style={{ ...champStyle(t), display: 'inline-flex', alignItems: 'center', gap: 8,
            fontWeight: 600, cursor: 'pointer', height: 38 }}>
          <RefreshCw size={14} style={{ animation: chargement ? 'spin 1s linear infinite' : 'none' }} />
          Actualiser
        </button>
      </div>

      <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 14,
        padding: 14, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: t.textMuted }}>Action</span>
          <select value={action} onChange={(e) => setAction(e.target.value)} style={champStyle(t)}>
            <option value="">Toutes</option>
            {Object.entries(libelles).map(([code, libelle]) => (
              <option key={code} value={code}>{libelle}</option>
            ))}
          </select>
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: t.textMuted }}>Entreprise</span>
          <select value={entreprise} onChange={(e) => setEntreprise(e.target.value)} style={champStyle(t)}>
            <option value="">Toutes</option>
            {entreprises.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
          </select>
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: t.textMuted }}>Période</span>
          <select value={fenetre} onChange={(e) => setFenetre(Number(e.target.value))} style={champStyle(t)}>
            {FENETRES.map((f) => <option key={f.valeur} value={f.valeur}>{f.libelle}</option>)}
          </select>
        </label>
      </div>

      <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 14, overflow: 'hidden' }}>
        {erreur && <p style={{ margin: 0, padding: 18, fontSize: 14, color: t.dangerText }}>{erreur}</p>}
        {!erreur && chargement && (
          <p style={{ margin: 0, padding: 18, fontSize: 14, color: t.textMuted }}>Lecture du journal…</p>
        )}
        {!erreur && !chargement && lignes.length === 0 && (
          <p style={{ margin: 0, padding: 18, fontSize: 14, color: t.textMuted }}>
            Aucun acte enregistré sur cette période. Le journal a commencé le 24 septembre 2026 :
            ce qui a été fait avant n&rsquo;y figure pas.
          </p>
        )}
        {!erreur && !chargement && lignes.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: t.bgTableHeader }}>
                {['Quand', 'Acte', 'Sur qui', 'Détail', 'Par', 'Adresse'].map((titre) => (
                  <th key={titre} style={{ textAlign: 'left', padding: '11px 16px', fontSize: 11,
                    fontWeight: 700, color: t.textMuted, textTransform: 'uppercase',
                    letterSpacing: '0.5px', borderBottom: `1px solid ${t.border}` }}>{titre}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lignes.map((l) => {
                const Icone = ICONES[l.action] || ShieldAlert;
                const sensible = sensibles.includes(l.action);
                return (
                  <tr key={l.id} style={{ borderBottom: `1px solid ${t.borderLight}`,
                    background: sensible ? t.dangerSoft : 'transparent' }}>
                    <td style={{ padding: '11px 16px', fontSize: 13, color: t.text, whiteSpace: 'nowrap' }}>
                      {l.quand}
                    </td>
                    <td style={{ padding: '11px 16px', fontSize: 13 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8,
                        color: sensible ? t.dangerText : t.text, fontWeight: 600 }}>
                        <Icone size={14} />
                        {libelles[l.action] || l.action}
                      </span>
                    </td>
                    <td style={{ padding: '11px 16px', fontSize: 13, color: t.textSecondary }}>
                      {l.cible_libelle || l.company_name || (l.entity_id ? `#${l.entity_id}` : '—')}
                    </td>
                    <td style={{ padding: '11px 16px', fontSize: 13, color: t.textSecondary }}>
                      {l.resume || '—'}
                    </td>
                    <td style={{ padding: '11px 16px', fontSize: 13, color: t.textSecondary }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <User size={13} style={{ color: t.textMuted }} />
                        {l.acteur_email || '—'}
                      </span>
                    </td>
                    <td style={{ padding: '11px 16px', fontSize: 13, color: t.textSecondary,
                      fontVariantNumeric: 'tabular-nums' }}>
                      {l.ip || '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <p style={{ fontSize: 12, color: t.textMuted, margin: 0, lineHeight: 1.6 }}>
        Le journal démarre le 24 septembre 2026 : les actes antérieurs n&rsquo;ont jamais été
        enregistrés. Un mot de passe temporaire n&rsquo;y figure jamais — seulement le fait
        qu&rsquo;il a été remis, à qui, et par qui.
      </p>
    </div>
  );
};

export default ActionsPrivilegiees;
